import styled from "styled-components";
import Textbox from "../../../components/form/input";
import SubmitButton from "../../../components/form/submit";
import { AxiosError } from "axios";
import React, { FormEvent, LegacyRef, useState } from "react";
import { BE_BASE_URL } from "../../../utils/config";
import { useTranslation } from "react-i18next";
import { Color } from "../../../constants/color";
import Label from "../../../components/label";
import { goToPage } from "../../../utils/utils";
import { axiosInstance, useAuth } from "../../../provider/authProvider";
import { Bounce, ToastContainer, toast } from "react-toastify";

const FormWrapper = styled.form<{ $error: boolean }>`
  order: 2;
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
  position: relative;
  min-width: 25rem;
  min-height: 20rem;
  margin: 3.2rem auto auto;
  border-radius: 2rem;
  background-color: ${Color.WHITE};
  box-shadow: 0 0 8px 4px #e2e2e2;
  button {
    margin: auto;
  }
`;

const Header = styled.h1`
  background-color: ${Color.LIGHT_BLUE};
  border-radius: 2rem 2rem 0 0;
  color: ${Color.WHITE};
  text-align: center;
  line-height: 80px;
  font-size: 20px;
  font-family: "Avenir Next Bold", sans-serif;
  margin: 2px 2px 0 2px;
`;

const ErrorMessage = styled.div<{ $error: boolean }>`
  display: ${(props) => (props.$error ? "unset" : "none")};
  text-align: center;
  max-width: 20rem;
  height: 2rem;
  margin-top: 0.6rem;
  margin-left: 2.5rem;
  background-color: ${Color.PINK};
  border-radius: 0.6rem;
  border: 2px solid ${Color.RED};
  div {
    margin-top: 0.5rem;
  }
`;

const FirstLogin = () => {
  const { t } = useTranslation();
  const { setToken } = useAuth();
  const formRef: LegacyRef<HTMLFormElement> | undefined = React.createRef();
  const [error, setError] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const changePassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validazione: controlla che nuova password e conferma siano uguali
    if (newPassword !== confirmPassword) {
      toast.error(t("Le password non coincidono"), {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
        theme: "colored",
      });
      return;
    }

    try {
      const payload = {
        oldPassword: oldPassword,
        newPassword: newPassword,
      };
      const response = await axiosInstance.post(
        BE_BASE_URL + "/user/updatePassword",
        payload
      );
      if (response.status === 200) {
        /*
         Cambiando la password il backend invalida i token emessi prima, compreso quello con
         cui questa scheda ha appena fatto la richiesta, e ne restituisce uno nuovo: va adottato
         subito, prima di andare altrove, altrimenti la prossima chiamata risponde 401 e riporta
         al login. Se il campo manca - un backend di prima del cambio - il token vecchio vale
         ancora e si resta su quello, come prima.
        */
        const tokenNuovo = response.data?.token;
        if (typeof tokenNuovo === "string" && tokenNuovo) {
          setToken(tokenNuovo);
        }
        const message = response.data.message;
        toast.success(message, {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
          theme: "colored",
        });
        setTimeout(() => {
          goToPage("dashboard");
        }, 5000);
      }
    } catch (e: unknown) {
      const err = e as AxiosError;
      if (err.response && err.response.data) {
        const errorData = err.response.data as any;
        toast.error(errorData.message, {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
          theme: "colored",
        });
      } else {
        setError(true);
      }
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick={true}
        pauseOnHover={true}
        draggable={true}
        theme="colored"
        transition={Bounce}
      />
      <FormWrapper
        id="first-login"
        ref={formRef}
        onSubmit={changePassword}
        $error={error}
      >
        <Header>{t("reset-password-placeholder-new")}</Header>
        <Textbox
          alt="oldPassword"
          label={t("Vecchia Password")}
          form="first-login"
          autocomplete="current-password"
          name="oldPassword"
          placeholder={t("Inserisci la password attuale")}
          type="password"
          onChange={(e) => setOldPassword(e.target.value)}
        />
        <Textbox
          alt="newPassword"
          label={t("Nuova Password")}
          form="first-login"
          autocomplete="new-password"
          name="newPassword"
          placeholder={t("Inserisci la nuova password")}
          type="password"
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <Textbox
          alt="confirmPassword"
          label={t("Conferma Password")}
          form="first-login"
          autocomplete="new-password"
          name="confirmPassword"
          placeholder={t("Conferma la nuova password")}
          type="password"
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <SubmitButton
          label={t("Cambia Password")}
          form="first-login"
          name="submit change password"
          style={{ padding: "0.5rem 1.5rem", marginBottom: "1.5rem" }}
        />
        <ErrorMessage $error={error}>
          <Label
            text={t("Si è verificato un errore")}
            color={Color.RED}
            fontSize={"13px"}
          />
        </ErrorMessage>
      </FormWrapper>
    </>
  );
};

export default FirstLogin;
